<template>
  <l-modal
    :title="t(`menu-availability.disable.${item.type}.title`)"
    :button-text="t('ctas.confirm')"
    :button-enabled="canSave"
    @action="disableItem"
    @close="() => $emit('close')"
  >
    <div class="flex flex-col gap-6">
      <div class="grid grid-cols-2 gap-2">
        <div v-for="option in options" :key="option">
          <l-card-radio name="refillPolicy" :value="option" v-model="selectedOption">
            <div class="font-body text-sm text-n-0 flex flex-col gap-2">
              <div class="font-bold">
                {{ t(`menu-availability.disable.options.${option}.title`) }}
              </div>
              <div class="text-xs">
                {{ t(`menu-availability.disable.options.${option}.description`) }}
              </div>
            </div>
          </l-card-radio>
        </div>
      </div>
      <l-selector-list
        v-model="selectedValue"
        :options="temporallyValuesMap"
        :disabled="selectedOption !== 'temporally'"
      />
    </div>
  </l-modal>
</template>

<script setup lang="ts">
import { addMinutes } from 'date-fns'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { LCardRadio, LModal, LSelectorList } from '@last/core-ui/paprika'

import { useStockStore } from '@/store/stock'
import type { DisablingMode, StockItem } from '@/types'

const emit = defineEmits<{
  close: []
}>()
const props = defineProps<{ item: StockItem }>()
const stockStore = useStockStore()
const { t } = useI18n()

const options = ['indefinite', 'temporally'] as const
const selectedOption = ref<DisablingMode>('indefinite')

const temporallyValues = ['30m', '1h', 'until-closure']
const temporallyValuesMap = computed(() => {
  return temporallyValues.map(value => ({
    id: value,
    label: t(`time-selector.${value}`)
  }))
})
const selectedValue = ref<string | undefined>()

const canSave = computed(() => {
  return selectedOption.value === 'indefinite' || selectedValue.value !== undefined
})

function parseValue(value: string | undefined) {
  if (!value) return { until: undefined, untilClosure: false }
  const now = new Date()
  if (value === '30m') return { until: addMinutes(now, 30), untilClosure: false }
  if (value === '1h') return { until: addMinutes(now, 60), untilClosure: false }
  if (value === 'until-closure') return { until: undefined, untilClosure: true }
  return { until: undefined, untilClosure: false }
}

function disableItem() {
  const { until, untilClosure } = parseValue(selectedValue.value)
  stockStore.toggleItem(props.item, false, selectedOption.value, {
    until,
    untilClosure
  })
  emit('close')
}

watch(selectedOption, newValue => {
  if (newValue === 'indefinite') selectedValue.value = undefined
})
</script>
